import {
  Button,
  Checkbox,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
} from "@chakra-ui/react";
import { FormRow, type IOption, Select, TextField } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  type BillingEntityModalFragment,
  type Client,
  useCompanyDataAutocompleteQuery,
  useCompanyDataByRegistrationNoLazyQuery,
  useCreateBillingEntityMutation,
  useOptionsForBillingEntityQuery,
  useUpdateBillingEntityMutation,
} from "@src/__generated__/graphql";
import { AutocompleteField } from "@src/components/ui-kit/AutocompleteField";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import {
  fieldToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { BankAccountSelect } from "@src/widgets/BankAccountSelect";
import { CompanySelect } from "@src/widgets/CompanySelect";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { FormBillingEntity } from "./form/FormBillingEntity";

interface ModalBillingEntityProps {
  isOpen: boolean;
  onClose: () => void;

  client: Pick<Client, "id" | "name"> & {
    workspaceCompany: { id: Client["workspaceCompany"]["id"] };
  };
  billingEntity?: BillingEntityModalFragment;
  onSubmit: (payload: BillingEntityModalFragment) => void;
}

const ModalBillingEntity = (props: ModalBillingEntityProps) => {
  const { workspaceStore } = useStore();
  const [form] = useState(() => new FormBillingEntity());

  useEffect(() => {
    if (!props.billingEntity) {
      form.company_id.onChange(props.client.workspaceCompany.id);
      form.presetBankAccount(props.client.workspaceCompany.id);
      return;
    }
    form._form.reset();

    form.legal_name.onChange(props.billingEntity.legal_name);
    form.internal_name.onChange(props.billingEntity.internal_name);
    form.street.onChange(props.billingEntity.street);
    form.city.onChange(props.billingEntity.city);
    form.zip.onChange(props.billingEntity.zip);
    form.country_id.onChange(props.billingEntity.country.id);
    form.registration_no.onChange(props.billingEntity.registration_no ?? "");
    form.tax_no.onChange(props.billingEntity.tax_no ?? "");
    form.vat_no.onChange(props.billingEntity.vat_no ?? "");
    form.payment_due.onChange(String(props.billingEntity.payment_due));
    form.invoice_email.onChange(props.billingEntity.invoice_email ?? "");
    form.note.onChange(props.billingEntity.note ?? "");
    form.applies_vat.onChange(props.billingEntity.applies_vat);
    form.default_currency_id.onChange(props.billingEntity.defaultCurrency.id);
    form.language.onChange(props.billingEntity.language);
    form.company_id.onChange(props.billingEntity.workspaceCompany.id);
    form.bank_account_id.onChange(props.billingEntity.workspaceBankAccount?.id);
    form.disable_email_notification.onChange(
      props.billingEntity.disable_email_notification,
    );
  }, [props.billingEntity]);

  const { data, loading: loadingOptions } = useOptionsForBillingEntityQuery({
    fetchPolicy: "cache-and-network",
    skip: !props.isOpen,
  });

  const beforeSubmit = async () => {
    const { hasError } = await form._form.validate();

    if (hasError) return;

    const serialized = form.serialize();

    if (props.billingEntity?.id) {
      update({
        variables: { input: { ...serialized, id: props.billingEntity.id } },
      });
    } else {
      create({
        variables: { input: { ...serialized, client_id: props.client.id } },
      });
    }
  };

  const onSubmitWrapper = (payload: BillingEntityModalFragment) => {
    props.onSubmit(payload);
    form._form.reset();
  };

  const [create, { loading: loadingCreate }] = useCreateBillingEntityMutation({
    onCompleted(response) {
      if (response.createBillingEntity) {
        onSubmitWrapper(response.createBillingEntity);
      }
    },
    ...onError(),
  });

  const [update, { loading: loadingUpdate }] = useUpdateBillingEntityMutation({
    onCompleted(response) {
      if (response.updateBillingEntity) {
        onSubmitWrapper(response.updateBillingEntity);
      }
    },
    ...onError(),
  });

  const { loading: autocompleteLegalNameLoading } =
    useCompanyDataAutocompleteQuery({
      skip: form.legal_name.value.trim().length < 3,
      variables: {
        input: form.legal_name.value.trim(),
        country_id:
          form.country_id.value !== ""
            ? form.country_id.value
            : workspaceStore.settings?.country.id!,
      },
      onCompleted({ getAutocompleteForCompanyData }) {
        form.setAutoCompleteNameOptions(getAutocompleteForCompanyData.results);
      },
    });

  const { loading: autocompleteRegistrationNoLoading } =
    useCompanyDataAutocompleteQuery({
      skip: form.registration_no.value.trim().length < 6,
      variables: {
        input: form.registration_no.value.trim(),
        country_id:
          form.country_id.value !== ""
            ? form.country_id.value
            : workspaceStore.settings?.country.id!,
      },
      onCompleted({ getAutocompleteForCompanyData }) {
        form.setAutoCompleteRegistrationNoOptions(
          getAutocompleteForCompanyData.results,
        );
      },
    });

  const [fetchCompanyData] = useCompanyDataByRegistrationNoLazyQuery({
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      if (data.getCompanyDataByRegistrationNo) {
        form.setCompanyData(data.getCompanyDataByRegistrationNo);
      }
    },
  });

  const handleSelect = (value: string) => {
    fetchCompanyData({
      variables: {
        registration_no: value,
        country_id:
          form.country_id.value !== ""
            ? form.country_id.value
            : workspaceStore.settings?.country.id!,
      },
    });
  };

  const [countryOptions, languageOptions] = useMemo<
    [IOption[], IOption[]]
  >(() => {
    const countryOptions =
      data?.countrySimpleMap.map((i) => ({ label: i.name, value: i.id })) || [];
    const languageOptions =
      data?.languagesForInvoicePdf.map((i) => ({ label: i, value: i })) || [];
    return [countryOptions, languageOptions];
  }, [data?.countrySimpleMap]);

  const currencyOptions = useMemo<IOption[]>(
    () =>
      workspaceStore.currencies.map((i) => ({ label: i.code, value: i.id })),
    [workspaceStore.currencies],
  );

  const title = useMemo(() => {
    if (props.billingEntity?.id) {
      return t`Edit billing address for ${props.client.name}`;
    }
    return t`Add billing address for ${props.client.name}`;
  }, [props.billingEntity, props.client]);

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={() => {
        props.onClose();
        form._form.reset();
      }}
      size="2xl"
      trapFocus={false}
    >
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormRow title={t`Legal name`}>
            <AutocompleteField
              {...fieldToInputProps(form.legal_name)}
              label=""
              onSelect={({ value }) => handleSelect(value)}
              charCount={3}
              autoCompleteOptions={form.autoCompleteNameOptions.map(
                (option) => ({
                  label: (
                    <div>
                      <div className="font-bold">{option.name}</div>
                      <div className="text-gray-600 text-xl font-semibold">
                        <Trans>Reg. No: {option.reg_no}</Trans>
                      </div>
                      <div className="text-gray-600 text-xl font-semibold">
                        {option.city}
                      </div>
                    </div>
                  ),
                  value: option.reg_no,
                }),
              )}
              loading={autocompleteLegalNameLoading}
              onBlur={({ target }) => {
                if (form.internal_name.value.length > 0) return;
                form.internal_name.onChange(target.value);
              }}
            />
          </FormRow>

          <FormRow title={t`Title`}>
            <TextField {...fieldToInputProps(form.internal_name)} />
          </FormRow>

          <FormRow title={t`Street`}>
            <TextField {...fieldToInputProps(form.street)} />
          </FormRow>

          <FormRow title={t`City`}>
            <TextField id="field-city" {...fieldToInputProps(form.city)} />
          </FormRow>

          <FormRow title={t`Zip`}>
            <TextField id="field-zip" {...fieldToInputProps(form.zip)} />
          </FormRow>

          <FormRow title={t`Country`}>
            <Select
              asPortal
              placeholder={t`Select country`}
              isLoading={loadingOptions}
              {...fieldToSelectProps(form.country_id, countryOptions)}
            />
          </FormRow>

          <FormRow title={t`Reg. no`}>
            <AutocompleteField
              {...fieldToInputProps(form.registration_no)}
              label=""
              onSelect={({ value }) => handleSelect(value)}
              charCount={6}
              autoCompleteOptions={form.autoCompleteRegistrationNoOptions.map(
                (option) => ({
                  label: (
                    <div>
                      <div className="font-bold">{option.name}</div>
                      <div className="text-gray-600 text-xl font-semibold">
                        <Trans>Reg. No: {option.reg_no}</Trans>
                      </div>
                      <div className="text-gray-600 text-xl font-semibold">
                        {option.city}
                      </div>
                    </div>
                  ),
                  value: option.reg_no,
                }),
              )}
              loading={autocompleteRegistrationNoLoading}
            />
          </FormRow>

          <FormRow title={t`Tax ID`}>
            <TextField {...fieldToInputProps(form.tax_no)} />
          </FormRow>

          <FormRow title={t`VAT no.`}>
            <TextField {...fieldToInputProps(form.vat_no)} />
          </FormRow>

          <FormRow title={t`Payment due`}>
            <TextField
              type="number"
              step={1}
              {...fieldToInputProps(form.payment_due)}
            />
          </FormRow>

          <FormRow title={t`Invoice e-mail addresses`}>
            <TextField
              {...fieldToInputProps(form.invoice_email)}
              type="email"
            />
            <HStack align="center" mt="2">
              <Icon name="info-circle" />
              <Text color="gray.500" fontSize="sm" lineHeight="1">
                <Trans>
                  If you use multiple emails, please separate them with ", "
                </Trans>
              </Text>
            </HStack>
          </FormRow>

          <FormRow title={t`Applies VAT`}>
            <Checkbox
              isChecked={form.applies_vat.value}
              onChange={(event) =>
                form.applies_vat.onChange(event.target.checked)
              }
            />
          </FormRow>

          <FormRow title={t`Default currency`}>
            <Select
              asPortal
              placeholder={t`Select currency`}
              {...fieldToSelectProps(form.default_currency_id, currencyOptions)}
            />
          </FormRow>

          <FormRow
            title={t`Preferred legal entity`}
            display={
              workspaceStore.hasMultipleCompanies(form.company_id.value) ||
              form.company_id.hasError
                ? "flex"
                : "none"
            }
          >
            <CompanySelect
              field={form.company_id}
              onChange={form.presetBankAccount}
            />
          </FormRow>

          <FormRow
            title={t`Preferred IBAN`}
            display={form.showBankAccountSelect ? "flex" : "none"}
          >
            <BankAccountSelect
              field={form.bank_account_id}
              companyId={form.company_id.$}
            />
          </FormRow>

          <FormRow title={t`Language on invoice`}>
            <Select
              asPortal
              placeholder={t`Select language`}
              isLoading={loadingOptions}
              {...fieldToSelectProps(form.language, languageOptions)}
            />
          </FormRow>

          <FormRow title={t`Note`}>
            <TextField {...fieldToInputProps(form.note)} />
          </FormRow>

          <FormRow title={t`Disable email notifications`}>
            <Switch
              isChecked={form.disable_email_notification.value}
              onChange={(event) =>
                form.disable_email_notification.onChange(event.target.checked)
              }
            />
          </FormRow>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="grey" onClick={props.onClose} variant="outline">
            <Trans>Cancel</Trans>
          </Button>
          <Button
            ml={3}
            isDisabled={form._form.hasError}
            isLoading={loadingCreate || loadingUpdate}
            onClick={beforeSubmit}
          >
            <Trans>Save</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default observer(ModalBillingEntity);
